import moment from "moment";
import React from "react";
import { validateEmptyOrNull } from "./libs";
import { regionMap } from "../constants/ShipmentConstants";

// Shared utility functions
export const formatDeliveryStatus = (status) => {
	if (!status) return "";
	const statusMap = {
		Intransit: "In Transit",
	};
	return statusMap[status] || status;
};

export const formatMultipleValues = (value) => {
	if (!value) return "";

	const delimiter = value.includes("|") ? "|" : ",";

	// Split the input string by "|", trim each item, and create a Set to filter unique values
	const uniqueValues = [...new Set(value.split(delimiter).map((item) => item.trim()))];

	return uniqueValues.map((item, index) => (
		<React.Fragment key={index}>
			{index > 0 && <br />}
			{item}
		</React.Fragment>
	));
};

export const formatTimestampText = (timestapText) => {
    return timestapText ? moment(timestapText, "YYYYMMDDHHmmss").format("MM/DD/YYYY HH:mm:ss"): "";
}

export const convertUTCTimeToLocalTime = (UTCDate) => {
	var stillUtc = moment.utc(UTCDate, "YYYYMMDDHHmmss").toDate();
	const formattedLocalDate = moment.utc(stillUtc).local().format("MM/DD/YYYY HH:mm:ss");

	return formattedLocalDate ? formattedLocalDate : "";
};

export const extractDateAndTimeFromDateText = (dateText)=> {
    const arr = convertUTCTimeToLocalTime(dateText).split(" ");
    return {
        date: arr?.[0] ? arr[0] : "",
        time: arr?.[1] ? arr[1] : "",
    }
};

export const roundToTwoDecimalPlaces = (value) => {
	if (validateEmptyOrNull(value)) {
		return ''
	}

	const number = Number(value);
	if (isNaN(number)) {
		throw new Error("Input is not a valid number");
	}
	return Number(number.toFixed(2));
};

export const removeDuplicateShipments = (shipments) => {
	const uniqueMap = new Map();

	shipments.forEach((shipment) => {
		// Use composite key of just DeliveryNumber and NBNumber
		const key = `${shipment.DeliveryNumber}-${shipment.NBNumber}`;

		// Keep the most recently updated record
		if (
			!uniqueMap.has(key) ||
			(shipment.UpdatedTime &&
				uniqueMap.get(key).UpdatedTime &&
				shipment.UpdatedTime > uniqueMap.get(key).UpdatedTime)
		) {
			uniqueMap.set(key, shipment);
		}
	});

	return Array.from(uniqueMap.values());
};


// Define the missing function
export const convertDateStringToArray = (dateString) => {
	if (!dateString) return null;
	// If dateString is already an array, return it
	if (Array.isArray(dateString)) return dateString;
	// If dateString is a comma-separated string, split it and return as array
	if (typeof dateString === 'string' && dateString.includes(',')) {
		return dateString.split(',');
	}
	// If dateString is a single date string, return as an array with one element
	return [dateString];
}

export const getMissedRowBackground = (row) => {
	const hasRescheduleDate = !validateEmptyOrNull(row.RescheduleDate);
	if (hasRescheduleDate) {
		return "rgba(6, 160, 248, 0.15)"; // Light blue for rescheduled
	} else if (row.DeliveryStatus === "Missed") {
		return "rgba(255, 0, 0, 0.15)"; // Light red for missed + not rescheduled
	}
	return null;
}

export const getLocalTime = () => {
    const now = new Date();
    // Format date with explicit options to ensure consistency across browsers
    // Use en-GB locale for DD/MM/YYYY format
    const formattedDate = now.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
    // Format time consistently
    const formattedTime = now.toLocaleTimeString('en-GB', { 
        hour12: false, 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit' 
    });
    
    const localTime = {
        date: formattedDate,
        time: formattedTime,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timezoneOffset: now.getTimezoneOffset()
    };
    
    return localTime;
};

export const getDisplayRegion = (region) => {
	if (!region || !/^COSTCO\s+/i.test(region)) return region || "";
	const normalized = region
		.replace(/^COSTCO\s+/i, "")
		.replace(/\b\w/g, (c) => c.toUpperCase())
		.replace(/(?<=\w)\w+/g, (c) => c.toLowerCase());
	return regionMap[normalized] || region;
};

export const normalizeKeyWithoutSpace = (value) => {
	if (validateEmptyOrNull(value)) return '';
	return value.trim().toLowerCase().replace(/\s+/g, '');
}
